import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  User, 
  Package, 
  Calendar, 
  Code, 
  FileText, 
  Download,
  Eye,
  Pen,
  Check,
  Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { t, i18n } = useTranslation();
  const { user, token, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingId, setSigningId] = useState(null);
  const [openSignDialog, setOpenSignDialog] = useState(null); // rental id when open
  const [signForm, setSignForm] = useState({ first_name: '', last_name: '', consent: false });
  const [signedAt, setSignedAt] = useState(new Date());
  const lang = i18n.language;

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [ordersRes, rentalsRes, licensesRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers }),
        axios.get(`${API}/rentals`, { headers }),
        axios.get(`${API}/licenses`, { headers })
      ]);
      setOrders(ordersRes.data);
      setRentals(rentalsRes.data);
      setLicenses(licensesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(price);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-CA');
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-gray-100 text-gray-800 border-gray-200',
      processing: 'bg-gray-200 text-gray-800 border-gray-300',
      shipped: 'bg-gray-100 text-gray-700 border-gray-200',
      delivered: 'bg-green-100 text-green-800 border-green-200',
      active: 'bg-green-100 text-green-800 border-green-200',
      returned: 'bg-gray-100 text-gray-600 border-gray-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
    };
    return <Badge className={`${colors[status] || 'bg-gray-100'} border`}>{status}</Badge>;
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${orderId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Facture téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const downloadContract = async (type, referenceId) => {
    try {
      const response = await axios.get(`${API}/contracts/${type}/${referenceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrat_${type}_${referenceId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Contrat téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const openSignFor = (rentalId) => {
    // Pre-fill from user name
    const nameParts = (user?.name || '').trim().split(/\s+/);
    const first = nameParts[0] || '';
    const last = nameParts.slice(1).join(' ') || '';
    setSignForm({ first_name: first, last_name: last, consent: false });
    setSignedAt(new Date());
    setOpenSignDialog(rentalId);
  };

  const signContract = async (type, referenceId) => {
    if (!signForm.first_name.trim() || !signForm.last_name.trim()) {
      toast.error('Veuillez remplir votre prénom et votre nom');
      return;
    }
    if (!signForm.consent) {
      toast.error('Vous devez accepter les termes du contrat');
      return;
    }
    try {
      setSigningId(referenceId);
      
      await axios.post(`${API}/contracts/${type}/${referenceId}/sign`, {
        first_name: signForm.first_name.trim(),
        last_name: signForm.last_name.trim(),
        consent: true,
        user_agent: navigator.userAgent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Contrat signé avec succès!');
      setOpenSignDialog(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && typeof detail === 'object') {
        toast.error(detail.message || 'Ce contrat a déjà été signé');
        setOpenSignDialog(null);
        fetchData(); // Refresh so the UI shows the signed badge
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la signature');
      }
    } finally {
      setSigningId(null);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 text-black animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen py-12 bg-gray-50" data-testid="account-page">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center">
              <User className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-black">{user?.name}</h1>
              <p className="text-gray-500">{user?.email}</p>
            </div>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="orders" className="space-y-6">
            <TabsList className="bg-white border border-gray-200">
              <TabsTrigger value="orders" className="data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-orders">
                <Package className="h-4 w-4 mr-2" />
                {t('account.orders')}
              </TabsTrigger>
              <TabsTrigger value="rentals" className="data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-rentals">
                <Calendar className="h-4 w-4 mr-2" />
                {t('account.rentals')}
              </TabsTrigger>
              <TabsTrigger value="licenses" className="data-[state=active]:bg-black data-[state=active]:text-white" data-testid="tab-licenses">
                <Code className="h-4 w-4 mr-2" />
                {t('account.licenses')}
              </TabsTrigger>
            </TabsList>

            {/* Orders Tab */}
            <TabsContent value="orders">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-32 w-full bg-gray-100" />
                  ))}
                </div>
              ) : orders.length === 0 ? (
                <Card className="bg-white border-gray-200">
                  <CardContent className="py-12 text-center">
                    <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune commande</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <Card key={order.id} className="bg-white border-gray-200" data-testid={`order-${order.id}`}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <p className="text-xs text-gray-400 font-mono">#{order.id.slice(0, 8)}</p>
                            <p className="text-gray-500 text-sm">{formatDate(order.created_at)}</p>
                          </div>
                          {getStatusBadge(order.status)}
                        </div>
                        <div className="space-y-2 mb-4">
                          {order.items?.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-gray-700">{item.product_name} x{item.quantity}</span>
                              <span className="text-gray-500 font-mono">{formatPrice(item.total_price)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                          <span className="font-semibold text-black">Total: {formatPrice(order.total)}</span>
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="border-gray-300 text-gray-700 hover:bg-gray-50"
                              onClick={() => downloadInvoice(order.id)}
                              data-testid={`download-invoice-${order.id}`}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Facture
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Rentals Tab */}
            <TabsContent value="rentals">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-32 w-full bg-gray-100" />
                  ))}
                </div>
              ) : rentals.length === 0 ? (
                <Card className="bg-white border-gray-200">
                  <CardContent className="py-12 text-center">
                    <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune location</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {rentals.map((rental) => (
                    <Card key={rental.id} className="bg-white border-gray-200" data-testid={`rental-${rental.id}`}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-black">{rental.product_name}</h3>
                            <p className="text-xs text-gray-400 font-mono">#{rental.id.slice(0, 8)}</p>
                          </div>
                          {getStatusBadge(rental.status)}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                          <div>
                            <p className="text-gray-400">Début</p>
                            <p className="text-gray-700">{formatDate(rental.start_date)}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Fin</p>
                            <p className="text-gray-700">{formatDate(rental.end_date)}</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                          <span className="font-semibold text-black">Total: {formatPrice(rental.total)}</span>
                          <div className="flex gap-2">
                            {!rental.contract_signed ? (
                              <Dialog open={openSignDialog === rental.id} onOpenChange={(open) => !open && setOpenSignDialog(null)}>
                                <DialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    className="bg-black text-white hover:bg-gray-800"
                                    onClick={() => openSignFor(rental.id)}
                                    data-testid={`sign-contract-${rental.id}`}
                                  >
                                    <Pen className="h-4 w-4 mr-2" />
                                    Signer contrat
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="bg-white border-gray-200 max-w-lg">
                                  <DialogHeader>
                                    <DialogTitle className="text-black">Signature électronique du contrat</DialogTitle>
                                    <DialogDescription className="text-gray-500">
                                      Veuillez inscrire votre prénom et votre nom, puis cocher la case de consentement.
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="py-4 space-y-4">
                                    <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 space-y-1">
                                      <p><strong>Équipement :</strong> {rental.product_name}</p>
                                      <p><strong>Période :</strong> {formatDate(rental.start_date)} → {formatDate(rental.end_date)}</p>
                                      <p><strong>Total :</strong> {formatPrice(rental.total)}</p>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="space-y-2">
                                        <Label htmlFor="sign-first-name" className="text-gray-700">Prénom *</Label>
                                        <Input
                                          id="sign-first-name"
                                          value={signForm.first_name}
                                          onChange={(e) => setSignForm({ ...signForm, first_name: e.target.value })}
                                          className="bg-white border-gray-300 text-black"
                                          placeholder="Prénom"
                                          data-testid="sign-first-name"
                                        />
                                      </div>
                                      <div className="space-y-2">
                                        <Label htmlFor="sign-last-name" className="text-gray-700">Nom *</Label>
                                        <Input
                                          id="sign-last-name"
                                          value={signForm.last_name}
                                          onChange={(e) => setSignForm({ ...signForm, last_name: e.target.value })}
                                          className="bg-white border-gray-300 text-black"
                                          placeholder="Nom de famille"
                                          data-testid="sign-last-name"
                                        />
                                      </div>
                                    </div>

                                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                                      <p className="text-gray-400 text-xs">Date et heure de la signature</p>
                                      <p className="font-mono">
                                        {signedAt.toLocaleString('fr-CA', {
                                          year: 'numeric', month: '2-digit', day: '2-digit',
                                          hour: '2-digit', minute: '2-digit', second: '2-digit'
                                        })}
                                      </p>
                                    </div>

                                    <div className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg bg-white">
                                      <Checkbox
                                        id={`consent-${rental.id}`}
                                        checked={signForm.consent}
                                        onCheckedChange={(checked) => setSignForm({ ...signForm, consent: !!checked })}
                                        data-testid="sign-consent-checkbox"
                                      />
                                      <Label htmlFor={`consent-${rental.id}`} className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                                        J'ai lu et j'accepte les termes et conditions du contrat de location. Je reconnais que cette signature électronique a la même valeur légale qu'une signature manuscrite.
                                      </Label>
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <Button
                                      variant="outline"
                                      onClick={() => setOpenSignDialog(null)}
                                      className="border-gray-300 text-gray-700 hover:bg-gray-50"
                                    >
                                      Annuler
                                    </Button>
                                    <Button
                                      onClick={() => signContract('rental', rental.id)}
                                      disabled={signingId === rental.id || !signForm.consent || !signForm.first_name.trim() || !signForm.last_name.trim()}
                                      className="bg-black hover:bg-gray-800 text-white disabled:opacity-50"
                                      data-testid="confirm-sign-btn"
                                    >
                                      {signingId === rental.id ? (
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      ) : (
                                        <Check className="h-4 w-4 mr-2" />
                                      )}
                                      Signer le contrat
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            ) : (
                              <Badge className="bg-green-100 text-green-800 border border-green-200">
                                <Check className="h-3 w-3 mr-1" />
                                Contrat signé
                              </Badge>
                            )}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="border-gray-300 text-gray-700 hover:bg-gray-50"
                              onClick={() => downloadContract('rental', rental.id)}
                              data-testid={`download-contract-${rental.id}`}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Contrat PDF
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Licenses Tab */}
            <TabsContent value="licenses">
              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-32 w-full bg-gray-100" />
                  ))}
                </div>
              ) : licenses.length === 0 ? (
                <Card className="bg-white border-gray-200">
                  <CardContent className="py-12 text-center">
                    <Code className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune licence</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {licenses.map((license) => (
                    <Card key={license.id} className="bg-white border-gray-200" data-testid={`license-${license.id}`}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-semibold text-black">{license.software_name}</h3>
                            <p className="text-xs text-gray-400 font-mono">
                              Licence {license.license_type === 'annual' ? 'Annuelle' : 'Mensuelle'}
                            </p>
                          </div>
                          {license.is_active ? (
                            <Badge className="bg-green-100 text-green-800 border border-green-200">Actif</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 border border-red-200">Inactif</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                          <div>
                            <p className="text-gray-400">Début</p>
                            <p className="text-gray-700">{formatDate(license.start_date)}</p>
                          </div>
                          <div>
                            <p className="text-gray-400">Prochain paiement</p>
                            <p className="text-gray-700">{formatDate(license.next_billing_date)}</p>
                          </div>
                        </div>
                        <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                          <span className="font-semibold text-black font-mono">
                            {formatPrice(license.current_price)}/{license.license_type === 'annual' ? 'an' : 'mois'}
                          </span>
                          <Button variant="outline" size="sm" className="border-gray-300 text-gray-700 hover:bg-gray-50">
                            Gérer
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
